import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { captureError } from '../utils/analytics';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    captureError(error, {
      boundary: 'app',
      component_stack: errorInfo?.componentStack,
    });

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Member-facing copy rules (design §5): honest — every claim here is
      // true (componentDidCatch reported the error to PostHog, which alerts
      // the team on new issues; "saved work" = persisted state only, since
      // unsynced local edits CAN be lost on reload). No developer jargon.
      return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg border border-neutral-200 shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" aria-hidden="true" />
              <h2 className="text-xl font-medium text-neutral-800" style={{ letterSpacing: '-0.02em' }}>
                This page hit a problem
              </h2>
            </div>

            <p className="text-neutral-600 mb-6">
              I've reported it automatically, and your saved work is untouched.
              Reloading usually clears it.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-semibold text-red-800 mb-2">Error Details (dev only):</h3>
                <pre className="text-sm text-red-700 whitespace-pre-wrap overflow-auto max-h-32">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-800 font-medium">Stack Trace</summary>
                    <pre className="text-xs text-red-600 mt-1 whitespace-pre-wrap overflow-auto max-h-32">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Reload is the reliable remedy (a broken chunk is only ever
                fixed by fresh HTML — "try again" re-renders the same failure
                for that class), so it takes the primary slot. */}
            <div className="flex space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-900 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
                Reload page
              </button>

              <button
                onClick={this.handleReset}
                className="flex items-center px-4 py-2 bg-white text-neutral-700 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
